import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, FileWarning, CalendarClock, ArrowRight, BadgeCheck } from "lucide-react";

const PILL = {
  red: "bg-red-50 text-red-700 border-red-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  yellow: "bg-yellow-50 text-yellow-700 border-yellow-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  navy: "bg-[#1A2744]/5 text-[#1A2744] border-[#1A2744]/20",
};

function Pill({ tone, children, testid }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold border rounded-full ${PILL[tone] || PILL.navy}`}
    >
      {children}
    </span>
  );
}

export function ComplianceCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/compliance/summary");
        if (alive) setData(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Failed to load");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <Card data-testid="compliance-card" className="border-gray-200">
        <CardContent className="p-6 text-sm text-gray-500">Loading compliance summary…</CardContent>
      </Card>
    );
  }
  if (err) {
    return (
      <Card data-testid="compliance-card" className="border-red-200 bg-red-50/40">
        <CardContent className="p-6 text-sm text-red-700">Compliance summary failed: {err}</CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const v = data.visas;
  const t = data.trials;
  const d = data.documents;
  const allClear = v.expired + v.urgent + v.warning + t.auto_flagged + t.ending_within_7d + d.employees_with_gaps === 0;

  return (
    <Card data-testid="compliance-card" className="border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-[#1A2744] to-[#0F1A30] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-[#C9A84C]/20 flex items-center justify-center">
            <ShieldAlert className="h-5 w-5 text-[#C9A84C]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#C9A84C] font-semibold">Action centre</div>
            <h3 className="font-heading text-lg font-bold text-white">Compliance dashboard</h3>
          </div>
        </div>
        <div className="text-[11px] text-white/60">As of {data.as_of}</div>
      </div>

      <CardContent className="p-0">
        {allClear && (
          <div data-testid="compliance-allclear" className="px-6 py-8 text-center">
            <BadgeCheck className="h-10 w-10 mx-auto text-emerald-500 mb-2" />
            <div className="font-medium text-[#1A2744]">All clear</div>
            <p className="text-sm text-gray-500 mt-1">No urgent visa, trial or document gaps right now.</p>
          </div>
        )}

        {!allClear && (
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">
            {/* Visas ----------------------------------------------------- */}
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CalendarClock className="h-4 w-4 text-[#C9A84C]" />
                  <span className="text-xs uppercase tracking-widest font-semibold text-gray-500">Visas</span>
                </div>
                <Link to="/admin/visas" className="text-[11px] text-[#1A2744] hover:underline flex items-center gap-1" data-testid="compliance-visa-link">
                  Open <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                <Pill tone={v.expired ? "red" : "emerald"} testid="compliance-visa-expired">
                  {v.expired} expired
                </Pill>
                <Pill tone={v.urgent ? "red" : "emerald"} testid="compliance-visa-urgent">
                  {v.urgent} ≤30d
                </Pill>
                <Pill tone={v.warning ? "amber" : "emerald"} testid="compliance-visa-warning">
                  {v.warning} ≤60d
                </Pill>
                <Pill tone={v.watch ? "yellow" : "emerald"} testid="compliance-visa-watch">
                  {v.watch} ≤90d
                </Pill>
              </div>
              {v.items?.length ? (
                <ul className="space-y-1.5 text-xs text-gray-700" data-testid="compliance-visa-list">
                  {v.items.map((it) => (
                    <li key={it.id} className="flex items-center justify-between gap-2">
                      <div className="truncate">
                        <span className="font-semibold text-[#1A2744]">{it.employee_code}</span>{" "}
                        <span className="text-gray-500">· {it.visa_type}</span>
                      </div>
                      <span className={`text-[10px] font-semibold whitespace-nowrap ${it.days_to_expiry < 0 ? "text-red-600" : it.days_to_expiry <= 30 ? "text-red-600" : it.days_to_expiry <= 60 ? "text-amber-600" : "text-yellow-700"}`}>
                        {it.days_to_expiry < 0 ? `${-it.days_to_expiry}d ago` : `${it.days_to_expiry}d`}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs text-gray-400">All visas comfortably current.</div>
              )}
            </div>

            {/* Trials ----------------------------------------------------- */}
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-[#C9A84C]" />
                  <span className="text-xs uppercase tracking-widest font-semibold text-gray-500">Trials</span>
                </div>
                <Link to="/admin/trials" className="text-[11px] text-[#1A2744] hover:underline flex items-center gap-1" data-testid="compliance-trial-link">
                  Open <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                <Pill tone={t.auto_flagged ? "red" : "emerald"} testid="compliance-trial-flagged">
                  {t.auto_flagged} need review
                </Pill>
                <Pill tone={t.ending_within_7d ? "amber" : "emerald"} testid="compliance-trial-ending">
                  {t.ending_within_7d} end ≤7d
                </Pill>
              </div>
              {t.items?.length ? (
                <ul className="space-y-1.5 text-xs text-gray-700" data-testid="compliance-trial-list">
                  {t.items.map((it) => (
                    <li key={it.id} className="flex items-center justify-between gap-2">
                      <div className="truncate">
                        <span className="font-semibold text-[#1A2744]">{it.employee_code}</span>{" "}
                        <span className="text-gray-500">· {it.user_name}</span>
                      </div>
                      <span className={`text-[10px] font-semibold whitespace-nowrap ${it.days_remaining < 0 ? "text-red-600" : it.days_remaining <= 7 ? "text-amber-600" : "text-gray-500"}`}>
                        {it.days_remaining < 0 ? `${-it.days_remaining}d over` : `${it.days_remaining}d left`}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs text-gray-400">No trial reviews due.</div>
              )}
            </div>

            {/* Documents -------------------------------------------------- */}
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileWarning className="h-4 w-4 text-[#C9A84C]" />
                  <span className="text-xs uppercase tracking-widest font-semibold text-gray-500">Documents</span>
                </div>
                <Link to="/admin/documents" className="text-[11px] text-[#1A2744] hover:underline flex items-center gap-1" data-testid="compliance-doc-link">
                  Open <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                <Pill tone={d.by_category?.CONTRACT ? "amber" : "emerald"} testid="compliance-doc-contract">
                  {d.by_category?.CONTRACT || 0} no contract
                </Pill>
                <Pill tone={d.by_category?.IRD ? "amber" : "emerald"} testid="compliance-doc-ird">
                  {d.by_category?.IRD || 0} no IRD
                </Pill>
                <Pill tone={d.by_category?.ID ? "amber" : "emerald"} testid="compliance-doc-id">
                  {d.by_category?.ID || 0} no ID
                </Pill>
              </div>
              {d.items?.length ? (
                <ul className="space-y-1.5 text-xs text-gray-700" data-testid="compliance-doc-list">
                  {d.items.map((it) => (
                    <li key={it.user_id} className="flex items-center justify-between gap-2">
                      <div className="truncate">
                        <span className="font-semibold text-[#1A2744]">{it.employee_code}</span>{" "}
                        <span className="text-gray-500">· {it.user_name}</span>
                      </div>
                      <span className="text-[10px] font-semibold text-amber-600 whitespace-nowrap">
                        missing {it.missing.join(", ")}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs text-gray-400">All required docs filed.</div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
